import React from 'react';
import { Box, IconButton } from '@mui/material';
import { Chip, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { EditRounded } from '@mui-symbols-material/w200';
import { InfoRounded } from '@mui-symbols-material/w300';
import { SummaryStepProps, StepCategory } from '../../types';
import { TypeDefinitionAttribute } from '../../../../codegen/graphql';
import { isGasBallastEnabled } from '../../../../utils/serviceDataValidation';
import { formatScanDate, getNetworkScanChipColors } from '../../../../config/networkScanConfig';

const CATEGORY_TITLES: Record<StepCategory, string> = {
    connection: 'Connection',
    specifications: 'Specifications',
    serviceData: 'Service Data',
};

const CATEGORY_STEP_INDEX: Record<StepCategory, number> = {
    connection: 0,
    specifications: 1,
    serviceData: 2,
};

const SummaryStep = ({ assetLocationId, formData, attributes, onEditStep, networkScanSummary, showCommissioningHint }: SummaryStepProps) => {
    const { t } = useTranslation();

    const getAttributeName = (index: string): string => {
        const attr = attributes.find((a: TypeDefinitionAttribute) => a.index === index);
        return attr?.name ?? index;
    };

    const isIsoDateString = (value: unknown): boolean => {
        if (typeof value !== 'string') return false;
        return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
    };

    const formatValue = (value: unknown): string => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (typeof value === 'boolean') {
            return value ? t('Yes') : t('No');
        }
        if (isIsoDateString(value)) {
            return moment(value as string).format('DD.MM.YY');
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const renderSection = (category: StepCategory, data: Record<string, unknown>) => {
        // The gas ballast type field only exists while the checkbox is checked —
        // don't list a value the form hides.
        const entries = Object.entries(data).filter(
            ([key]) => key !== 'gasBallastType' || isGasBallastEnabled(data)
        );

        if (entries.length === 0) {
            return null;
        }

        return (
            <Box key={category} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: '12px' }}>
                    <Typography level="headline">
                        {t(CATEGORY_TITLES[category])}
                    </Typography>
                    {onEditStep && (
                        <IconButton size="small" color="primary" onClick={() => onEditStep(CATEGORY_STEP_INDEX[category])}>
                            <EditRounded fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                {entries.map(([key, value], index) => (
                    <Box
                        key={key}
                        sx={{
                            display: 'flex',
                            py: 1,
                            borderBottom: index < entries.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography sx={{ width: '40%', color: 'text.secondary' }}>
                            {getAttributeName(key)}:
                        </Typography>
                        <Typography sx={{ flex: 1 }}>
                            {formatValue(value)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const hasAnyData = Object.values(formData).some(
        (categoryData) => Object.keys(categoryData).length > 0
    );

    const scanChip = getNetworkScanChipColors('GREEN');

    return (
        <Box>
            {networkScanSummary && (
                <Box data-testid="wizard-summary-network-scan" sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', pb: 2 }}>
                    <Chip size="sm" variant="soft" sx={{ backgroundColor: scanChip.bg, color: scanChip.text, fontWeight: 600 }}>
                        {t('Network scan OK')}
                    </Chip>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {t('Documented on {{name}}, {{date}}', { name: networkScanSummary.sourceName, date: formatScanDate(networkScanSummary.scanDate) })}
                    </Typography>
                </Box>
            )}
            {showCommissioningHint && (
                <Box
                    data-testid="wizard-summary-commissioning-hint"
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', p: '12px 16px', mb: 2, border: '1px solid #ccc', borderRadius: '4px' }}
                >
                    <InfoRounded sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {t("After creation the asset's SLA status is Inactive. Complete the commissioning on site to start the 24h connectivity verification.")}
                    </Typography>
                </Box>
            )}
            {hasAnyData ? (
                <>
                    {renderSection('connection', formData.connection)}
                    {renderSection('specifications', formData.specifications)}
                    {renderSection('serviceData', formData.serviceData)}
                </>
            ) : (
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                    {t('No data has been entered yet')}
                </Typography>
            )}
        </Box>
    );
};

export default SummaryStep;
